import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchData, HTTP_STATUS, DmsModal } from 'dmsbasereact';
import { DmsAutocomplete } from '../DmsAutoComplete/DmsAutoComplete';
import dayjs from 'dayjs';
import {
  DmsAltBox,
  DmsButton,
  DmsSelect,
  DmsTextField,
  DmsAlertInfo,
  DmsAlertDanger,
  DmsAlertWarning,
  DmsCheckBox,
  DmsCalendar,
  intl,
} from 'dmsbasereact';
import { openPrescriptionPdfForPrint } from '../../utils/downloadPrescriptionPdf';
import PrescriptionPreviewModal from './PrescriptionPreviewModal';
import PrescriptionQuantityAlertModal from './PrescriptionQuantityAlertModal';
import { UserInstitutionSelect } from '../UserInstitutionSelect/UserInstitutionSelect';
import { TrainingModal } from '../TrainingModal';
import { getGenderEnum } from '../../constants/GenderEnum';
import { normalizeGender } from '../../utils/formatters';
import { useInstitutionStatus } from '../../hooks/useInstitutionStatus';
import { InstitutionStatusEnum } from '../../constants/InstitutionStatus';
import { NotDispensedReasonEnum, getNotDispensedReasonOptions } from '../../constants/NotDispensedReasonEnum';

import {
  Box,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
  Checkbox,
  FormGroup,
  Link,
} from '@mui/material';
import { LegalDocumentDialog } from '../LegalDocumentDialog';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { PatientDetailModal } from '../PatientDetailModal';

import { MENSTRUAL_CYCLE_STATUS } from '../../constants/MenstrualCycleStatus';
import { PRESCRIPTION_STATUS } from '../../constants/PrescriptionStatus';
import { getIndicationOptions, IndicationEnum } from '../../constants/Indication';
import { isExpired } from '../../utils/expiration';

/**
 * Convert a number to Portuguese words.
 * Handles numbers from 0 to 99.
 */
const numberToPortugueseWords = (n) => {
  if (!Number.isInteger(n) || n < 0) return n.toString();

  // Feminine forms for "cápsula" (feminine noun)
  const units = ["", "uma", "duas", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"];
  const teens = ["dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
  const tens = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];

  if (n === 0) return "zero";
  if (n < 10) return units[n];
  if (n < 20) return teens[n - 10];
  if (n < 100) {
    const ten = tens[Math.floor(n / 10)];
    const unit = units[n % 10];
    if (unit) return `${ten} e ${unit}`;
    return ten;
  }
  return n.toString();
};

export const PrescriptionForm = ({
  isModal = false,
  onSuccess = null,
  useCpfSearch = true,
  initialPatient = null
}) => {
  const location = useLocation();
  const autoSearchTriggered = useRef(false);
  const initialPatientProcessed = useRef(false);
  const successModalTimeoutRef = useRef(null);

  const [patient, setPatient] = useState('');
  const [patientData, setPatientData] = useState(null);
  const [cpf, setCpf] = useState('');
  const [dob, setDob] = useState(null);
  const [, setHasActivePrescription] = useState(false);
  const [activePrescriptionPk, setActivePrescriptionPk] = useState(null);
  const [, setIsDispensed] = useState(false);
  const [, setPrescriptionExpired] = useState(false);
  const [, setDaysRemainingForNewPrescription] = useState(0);
  const [, setNextPrescriptionDate] = useState(null);
  const [justification, setJustification] = useState('');
  const [nuvyorLocation, setNuvyorLocation] = useState('');
  const [isFemaleWithPotential, setIsFemaleWithPotential] = useState(false);
  const [menstrualCycle, setMenstrualCycle] = useState('');
  const [lastPregnancyTestDate, setLastPregnancyTestDate] = useState(null);
  const [previousPregnancyTestDate, setPreviousPregnancyTestDate] = useState(null);
  const [prescriptionDate, setPrescriptionDate] = useState(dayjs());
  const [expirationDate, setExpirationDate] = useState(null);
  const [institution, setInstitution] = useState('');
  const [blockPrescription, setBlockPrescription] = useState(false);
  const [hasExpiredNotDispensed, setHasExpiredNotDispensed] = useState(false);
  const [liabilityWaiverSigned, setLiabilityWaiverSigned] = useState(false);
  const [pregnancyTestConfirmed, setPregnancyTestConfirmed] = useState(false);
  const [highlyEffectiveMethods, setHighlyEffectiveMethods] = useState([]);
  const [barrierMethods, setBarrierMethods] = useState([]);
  const [medicalRecordsConfirmed, setMedicalRecordsConfirmed] = useState(false);
  const [patients, setPatients] = useState([]);
  const [posologies, setPosologies] = useState([]);
  const [selectedPosology, setSelectedPosology] = useState('');
  const [dosageNotes, setDosageNotes] = useState('');
  const [indication, setIndication] = useState('');
  const [indicationOther, setIndicationOther] = useState('');
  const [showInstitutionSelect, setShowInstitutionSelect] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showPatientNotFoundModal, setShowPatientNotFoundModal] = useState(false);
  const [showActivePrescriptionModal, setShowActivePrescriptionModal] = useState(false);
  const [activePrescriptionInfo, setActivePrescriptionInfo] = useState(null);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [selectedCrm, setSelectedCrm] = useState(null);
  const [createdPrescriptionPk, setCreatedPrescriptionPk] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLegalGuardianModal, setShowLegalGuardianModal] = useState(false);
  const [legalGuardianApproval, setLegalGuardianApproval] = useState(false);
  const [showTrainingWarningModal, setShowTrainingWarningModal] = useState(false);
  const [trainingWarningMessage, setTrainingWarningMessage] = useState('');
  const [, setTrainingExpirationInfo] = useState(null);
  const [, setShowTrainingExpirationWarning] = useState(false);
  const [allowAlternativeDispensation, setAllowAlternativeDispensation] = useState(false);
  const [dosageRectificationTermAccepted, setDosageRectificationTermAccepted] = useState(false);
  const [termDialogOpen, setTermDialogOpen] = useState(false);
  const [termDocument, setTermDocument] = useState(null);
  const [loadingTerm, setLoadingTerm] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [surplusInfo, setSurplusInfo] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showQuantityAlertModal, setShowQuantityAlertModal] = useState(false);
  const [previewPrescriptionData, setPreviewPrescriptionData] = useState(null);
  const [abovePackageInsertConfirmed, setAbovePackageInsertConfirmed] = useState(false);
  const [isAddingPatient, setIsAddingPatient] = useState(false);
  const [patientAdded, setPatientAdded] = useState(false);
  const [addPatientError, setAddPatientError] = useState(null);
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false);
  const [loadingPatientDetails, setLoadingPatientDetails] = useState(false);
  const [patientDetailsForEdit, setPatientDetailsForEdit] = useState(null);
  const [showPregnancyPotentialWarning, setShowPregnancyPotentialWarning] = useState(false);
  const [creatingPrescription, setCreatingPrescription] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitutionInactive, setSelectedInstitutionInactive] = useState(false);
  const [institutionApprovalStatus, setInstitutionApprovalStatus] = useState(null);
  const [isInstitutionInactive, setIsInstitutionInactive] = useState(false);
  const [doctorDeclarationMissing, setDoctorDeclarationMissing] = useState(false);
  const [highlyEffectiveMethodOptions, setHighlyEffectiveMethodOptions] = useState([]);
  const [barrierMethodOptions, setBarrierMethodOptions] = useState([]);

  // Get user's institution approval statuses
  const { institutionStatuses, isApprovedAtInstitution, getInstitutionStatus } = useInstitutionStatus();

  // Real-time validation errors for field-level validation
  const validationErrors = useMemo(() => {
    const errors = [];

    if (!patient) {
      errors.push({ field: "patient", message: intl.get("select_patient_first").d("Please select a patient first.") });
    }

    if (!selectedPosology) {
      errors.push({ field: "posology", message: intl.get("select_posology_first").d("Please select a posology.") });
    }

    if (!institution) {
      errors.push({ field: "institution", message: intl.get("select_institution_first").d("Please select an institution.") });
    }

    if (doctorDeclarationMissing) {
      errors.push({
        field: "doctorDeclaration",
        message: intl.get("DeclarationRequired").d("You must accept the Institution Link Declaration for this institution before creating prescriptions.")
      });
    }

    if (isFemaleWithPotential) {
      if (!menstrualCycle) {
        errors.push({ field: "menstrualCycle", message: intl.get("select_menstrual_cycle").d("Select the menstrual cycle status.") });
      }
      if (!lastPregnancyTestDate) {
        errors.push({ field: "pregnancyTestDate", message: intl.get("enter_pregnancy_test_date").d("Please enter the pregnancy test date.") });
      }
      if (!pregnancyTestConfirmed) {
        errors.push({ field: "pregnancyTestConfirmed", message: intl.get("confirm_pregnancy_test").d("Please confirm the negative pregnancy test.") });
      }
      if (highlyEffectiveMethods.length === 0) {
        errors.push({ field: "highlyEffectiveMethods", message: intl.get("select_highly_effective_method").d("Please select at least one highly effective contraceptive method.") });
      }
      if (barrierMethods.length === 0) {
        errors.push({ field: "barrierMethods", message: intl.get("select_barrier_method").d("Please select at least one barrier contraceptive method.") });
      }
      if (!medicalRecordsConfirmed) {
        errors.push({ field: "medicalRecordsConfirmed", message: intl.get("confirm_medical_records").d("Please confirm that the medical records documentation will be archived.") });
      }
    }

    if (!liabilityWaiverSigned) {
      errors.push({ field: "liabilityWaiverSigned", message: intl.get("accept_liability_waiver").d("Accept the liability waiver.") });
    }

    if (!indication) {
      errors.push({ field: "indication", message: intl.get("select_indication_first").d("Please select an indication.") });
    }

    if (indication === IndicationEnum.OTHER && !indicationOther) {
      errors.push({ field: "indicationOther", message: intl.get("enter_other_indication").d("Please specify the other indication.") });
    }

    if (!prescriptionDate) {
      errors.push({ field: "prescriptionDate", message: intl.get("enter_prescription_date").d("Please enter the prescription date.") });
    }

    return errors;
  }, [
    patient,
    selectedPosology,
    institution,
    doctorDeclarationMissing,
    isFemaleWithPotential,
    menstrualCycle,
    lastPregnancyTestDate,
    pregnancyTestConfirmed,
    highlyEffectiveMethods,
    barrierMethods,
    medicalRecordsConfirmed,
    liabilityWaiverSigned,
    indication,
    indicationOther,
    prescriptionDate
  ]);

  // Update fieldErrors state when validation errors change
  useEffect(() => {
    const errorMap = validationErrors.reduce((acc, err) => {
      acc[err.field] = err.message;
      return acc;
    }, {});
    setFieldErrors(errorMap);
  }, [validationErrors]);

  // Open patient edit modal
  const handleOpenEditPatient = async () => {
    if (!patientData?.pk) return;

    setLoadingPatientDetails(true);
    setEditPatientModalOpen(true);

    try {
      const response = await fetchData(`/patient/${patientData.pk}/details`, 'GET', true);
      if (response.status === HTTP_STATUS.OK_200) {
        setPatientDetailsForEdit(response.data);
      }
    } catch (error) {
      console.error('Error loading patient details:', error);
    }

    setLoadingPatientDetails(false);
  };

  // Save patient changes
  const handleSavePatient = async (patientPk, payload) => {
    try {
      const response = await fetchData(`/patient/${patientPk}`, 'PUT', true, payload);
      if (response.status === HTTP_STATUS.OK_200) {
        // Reload patient details after save
        const detailsResponse = await fetchData(`/patient/${patientPk}/details`, 'GET', true);
        if (detailsResponse.status === HTTP_STATUS.OK_200) {
          const updatedPatient = detailsResponse.data;
          setPatientDetailsForEdit(updatedPatient);
          // Update patientData with new info
          setPatientData(prev => ({
            ...prev,
            ...updatedPatient,
          }));

          // Update the patients array so reopening modal shows correct data
          setPatients(prev => prev.map(p =>
            p.pk === patientPk
              ? { ...p, ...updatedPatient }
              : p
          ));

          // Update pregnancy potential state based on updated patient data
          const hasPotential = normalizeGender(updatedPatient.gender) === getGenderEnum().F.value && updatedPatient.has_pregnancy_potential;
          setIsFemaleWithPotential(hasPotential);

          // Update menstrual cycle from patient data if available
          if (updatedPatient.menstrual_cycle_regular !== null && updatedPatient.menstrual_cycle_regular !== undefined) {
            setMenstrualCycle(updatedPatient.menstrual_cycle_regular ? MENSTRUAL_CYCLE_STATUS.REGULAR : MENSTRUAL_CYCLE_STATUS.IRREGULAR);
          }

          // Note: Contraceptive methods will be reloaded in onPatientUpdated callback
          // after PatientDetailModal finishes saving all changes (including contraceptive methods)

          // Close the edit modal after successful save
          setEditPatientModalOpen(false);
          setPatientDetailsForEdit(null);

          return updatedPatient;
        }
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error saving patient:', error);
      return null;
    }
  };

  // Close success modal and cancel auto-close timer
  const handleCloseSuccessModal = () => {
    // Cancel the auto-close timer if it exists
    if (successModalTimeoutRef.current) {
      clearTimeout(successModalTimeoutRef.current);
      successModalTimeoutRef.current = null;
    }
    setShowSuccessModal(false);
    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleAddPatientToDoctorList = async () => {
    if (!patientData) return;

    setIsAddingPatient(true);
    setAddPatientError(null);

    try {
      const response = await fetchData('/doctor/add_patient', 'POST', true, { patient_pk: patientData.pk });

      if (response.status === HTTP_STATUS.CREATED_201) {
        setPatientAdded(true);
      }
    } catch (error) {
      if (error.response && error.response.status === HTTP_STATUS.CONFLICT_409) {
        setPatientAdded(true); // Already in the list, so we can consider it "added"
        setAddPatientError(intl.get("patient_already_in_list").d("Patient is already in your list."));
      } else {
        setAddPatientError(intl.get("add_patient_error").d("Error adding patient to your list. Please try again."));
      }
    } finally {
      setIsAddingPatient(false);
    }
  };

  // Auto-search patient when navigating from MyPatientsPage with patient data
  useEffect(() => {
    if (useCpfSearch && location.state && !autoSearchTriggered.current) {
      const { patientCpf, patientDob } = location.state;
      if (patientCpf && patientDob) {
        autoSearchTriggered.current = true;
        setCpf(patientCpf);
        const dobDate = dayjs(patientDob);
        setDob(dobDate);

        // Auto-trigger search after a short delay to ensure state is updated
        setTimeout(async () => {
          try {
            setPatientAdded(false);
            setAddPatientError(null);
            const cleanedCpf = patientCpf.replace(/\D/g, '');
            const formattedDob = dobDate.format('YYYY-MM-DD');
            const response = await fetchData(`/patient/find_by_cpf_dob?cpf=${cleanedCpf}&dob=${formattedDob}`, 'GET', true);

            if (response.status === 200) {
              let foundPatient = response.data.data || response.data || response;
              if (foundPatient && foundPatient.pk) {
                // Fetch prescriptions for the found patient to check blocking rule
                try {
                  const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${foundPatient.pk}?show_all=true`, 'GET', true);
                  if (prescriptionsResponse.status === 200) {
                    const prescriptions = prescriptionsResponse.data?.data || [];
                    // Add prescriptions to patient data
                    foundPatient = {
                      ...foundPatient,
                      prescription: prescriptions
                    };
                  }
                } catch (error) {
                  console.error('Error fetching patient prescriptions:', error);
                  // Continue with patient data even if prescription fetch fails
                }
                processSelectedPatient(foundPatient);
              } else {
                processSelectedPatient(null);
                setShowPatientNotFoundModal(true);
              }
            } else {
              processSelectedPatient(null);
              setShowPatientNotFoundModal(true);
            }
          } catch (error) {
            console.error('Error finding patient:', error);
            processSelectedPatient(null);
            setShowPatientNotFoundModal(true);
          }
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, useCpfSearch]);

  // Process initial patient when provided via prop (modal context)
  useEffect(() => {
    const processInitialPatient = async () => {
      if (initialPatient && !initialPatientProcessed.current) {
        initialPatientProcessed.current = true;

        let patientWithPrescriptions = initialPatient;

        // Fetch prescriptions for the initial patient to check blocking rule
        if (initialPatient.pk) {
          try {
            const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${initialPatient.pk}?show_all=true`, 'GET', true);
            if (prescriptionsResponse.status === 200) {
              const prescriptions = prescriptionsResponse.data?.data || [];
              // Add prescriptions to patient data
              patientWithPrescriptions = {
                ...initialPatient,
                prescription: prescriptions
              };
            }
          } catch (error) {
            console.error('Error fetching initial patient prescriptions:', error);
            // Continue with patient data even if prescription fetch fails
          }
        }

        processSelectedPatient(patientWithPrescriptions);
      }
    };

    processInitialPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPatient]);

  useEffect(() => {
    const checkIfDoctor = async () => {
      try {
        const response = await fetchData('/prescription/check_doctor', 'GET', true);
        if (response.status === 200) {
          const doctorCheck = response.data;
          if (doctorCheck.is_doctor) {
            setDoctorInfo(doctorCheck);
            // Pre-select primary CRM if available_crms exists
            if (doctorCheck.available_crms && doctorCheck.available_crms.length > 0) {
              const primaryCrm = doctorCheck.available_crms.find(c => c.is_primary) || doctorCheck.available_crms[0];
              setSelectedCrm(primaryCrm);
            }
          }
        }
      } catch (error) {
        console.error('Error checking if user is doctor:', error);
      }
    };
    checkIfDoctor();
  }, []);

  // Check training completion on mount
  useEffect(() => {
    const checkTrainingCompletion = async () => {
      try {
        const response = await fetchData('/training_material/check-completion', 'GET', true);
        const data = response.data || response;

        if (data && !data.completed) {
          setTrainingWarningMessage(data.message || intl.get("training_required_for_prescription").d(
            "You must complete the required training to have full access to the system. Please complete the 'Prescrição e Dispensação' training before creating prescriptions."
          ));
          setShowTrainingWarningModal(true);
        } else if (data && data.completed) {
          // Training completed - check for expiration warning
          if (data.certificate_valid_until) {
            setTrainingExpirationInfo({
              validUntil: new Date(data.certificate_valid_until),
              daysRemaining: data.days_until_expiration,
              expiringSoon: data.expiring_soon
            });

            // Show warning if expiring within 30 days
            if (data.expiring_soon) {
              setShowTrainingExpirationWarning(true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking training completion:', error);
      }
    };
    checkTrainingCompletion();
  }, []);

  useEffect(() => {
    if (!useCpfSearch) {
      const fetchPatients = async () => {
        try {
          const response = await fetchData('/doctor/all_patients', 'GET', true);
          if (response.status === 200) {
            setPatients(response.data.data?.rows || response.data.data || []);
          }
        } catch (error) {
          console.error(error);
        }
      };
      fetchPatients();
    }
  }, [useCpfSearch]);

  // Fetch user institutions (includes is_approved status)
  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const response = await fetchData('/institution/user-linked', 'GET', true);
        if (response.status === HTTP_STATUS.OK_200) {
          // API returns array of institutions with is_approved field
          const institutionsData = response.data?.data || [];

          if (Array.isArray(institutionsData) && institutionsData.length > 0) {
            setInstitutions(institutionsData);
            // Auto-select if only one institution
            if (institutionsData.length === 1) {
              setInstitution(institutionsData[0].organization_branch_pk);
              setShowInstitutionSelect(false);
            } else if (institutionsData.length > 1) {
              setShowInstitutionSelect(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching institutions:', error);
      }
    };
    fetchInstitutions();
  }, []);

  // Check if user is approved at selected institution AND if institution itself is approved
  useEffect(() => {
    if (institution && institutions.length > 0) {
      // Find the selected institution in the institutions list
      const selectedInst = institutions.find(inst => inst.organization_branch_pk === institution);

      if (selectedInst) {
        // Check both: institution's own status AND user's approval at that institution
        const isInstitutionApproved = selectedInst.status === InstitutionStatusEnum.APPROVED;
        const institutionIsInactive = selectedInst.status === InstitutionStatusEnum.INACTIVE;
        const isUserApproved = selectedInst.is_approved;
        // Check if doctor field exists (null/false/true) - only for doctors
        // Treat null and false as "no declaration", only true means declared
        const hasDoctorDeclaration = selectedInst.doctor_declaration_accepted === true;

        // Check institution status: INACTIVE first, then approval status, then user approval, then doctor declaration
        if (institutionIsInactive) {
          // Scenario 1: Institution is INACTIVE (deactivated)
          // Show "institution inactive" message
          setSelectedInstitutionInactive(true);
          setIsInstitutionInactive(true);
          setInstitutionApprovalStatus(null);
          setDoctorDeclarationMissing(false);
        } else if (!isInstitutionApproved) {
          // Scenario 2: Institution itself is NOT approved (but not inactive)
          // Show "institution pending approval" message
          setSelectedInstitutionInactive(true);
          setIsInstitutionInactive(false);
          setInstitutionApprovalStatus(null); // null = institution not approved
          setDoctorDeclarationMissing(false);
        } else if (!isUserApproved) {
          // Scenario 3: Institution IS approved, but user is NOT approved at this institution
          // Show "user pending approval" message
          const approvalStatus = getInstitutionStatus(institution);
          setSelectedInstitutionInactive(true);
          setIsInstitutionInactive(false);
          setInstitutionApprovalStatus(approvalStatus || { status_label: intl.get("Pending").d("Pendente") }); // truthy = user not approved
          setDoctorDeclarationMissing(false);
        } else if (!hasDoctorDeclaration) {
          // Scenario 4: Institution and user approved, but doctor hasn't accepted declaration
          // Show "declaration required" message
          setSelectedInstitutionInactive(true);
          setIsInstitutionInactive(false);
          setInstitutionApprovalStatus(null);
          setDoctorDeclarationMissing(true);
        } else {
          // Scenario 5: Institution, user, and declaration all OK - no warning
          setSelectedInstitutionInactive(false);
          setIsInstitutionInactive(false);
          setInstitutionApprovalStatus(null);
          setDoctorDeclarationMissing(false);
        }
      } else {
        setInstitutionApprovalStatus(null);
        setSelectedInstitutionInactive(false);
        setIsInstitutionInactive(false);
        setDoctorDeclarationMissing(false);
      }
    } else {
      setInstitutionApprovalStatus(null);
      setSelectedInstitutionInactive(false);
      setIsInstitutionInactive(false);
    }
  }, [institution, institutions, institutionStatuses, isApprovedAtInstitution, getInstitutionStatus]);

  useEffect(() => {
    const fetchPosologies = async () => {
      try {
        const response = await fetchData('/posology/all_with_parts', 'GET', true);
        if (response.status === HTTP_STATUS.OK_200) {
          const posologiesData = response.data?.data || response.data || [];
          if (Array.isArray(posologiesData)) {
            setPosologies(posologiesData);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchPosologies();
  }, []);

  // Fetch term document for alternative dispensation
  const fetchTermDocument = async () => {
    setLoadingTerm(true);
    try {
      const response = await fetchData("/legal-document/public/dosage_rectification_term", 'GET', false);
      if (response.status === HTTP_STATUS.OK_200) {
        setTermDocument(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch dosage rectification term:", error);
    } finally {
      setLoadingTerm(false);
    }
  };

  // Auto-calculate expiration date based on patient type and dates
  useEffect(() => {
    if (isFemaleWithPotential && lastPregnancyTestDate) {
      // Female with pregnancy potential: 6 days from pregnancy test date
      setExpirationDate(lastPregnancyTestDate.add(6, 'day'));
    } else if (!isFemaleWithPotential && prescriptionDate) {
      // Other patients: 19 days from prescription date
      setExpirationDate(prescriptionDate.add(19, 'day'));
    } else {
      setExpirationDate(null);
    }
  }, [isFemaleWithPotential, lastPregnancyTestDate, prescriptionDate]);

  // Fetch contraceptive method options on component mount
  useEffect(() => {
    fetchContraceptiveMethodOptions();
  }, []);

  // Fetch last pregnancy test date from previous prescriptions
  const fetchLastPregnancyTest = async (patientPk) => {
    try {
      const response = await fetchData(`/prescription/patient/${patientPk}/last-pregnancy-test`, 'GET', true);
      if (response.status === HTTP_STATUS.OK_200 && response.data?.data?.pregnancy_test_date) {
        setPreviousPregnancyTestDate(dayjs(response.data.data.pregnancy_test_date));
      } else {
        setPreviousPregnancyTestDate(null);
      }
    } catch (error) {
      console.error('Error fetching last pregnancy test:', error);
      setPreviousPregnancyTestDate(null);
    }
  };


  const fetchPatientContraceptiveMethods = async (patientPk) => {
    try {
      const response = await fetchData(`/patient_contraceptive_method/get_all_methods/${patientPk}`, 'GET', true);
      if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
        const { highly_effective_methods, barrier_methods } = response.data.data;
        // Extract only the PKs since the checkboxes use PKs as values
        const highlyEffectivePks = (highly_effective_methods || []).map(m => m.pk);
        const barrierPks = (barrier_methods || []).map(m => m.pk);
        setHighlyEffectiveMethods(highlyEffectivePks);
        setBarrierMethods(barrierPks);
      } else {
        setHighlyEffectiveMethods([]);
        setBarrierMethods([]);
      }
    } catch (error) {
      console.error('Error fetching patient contraceptive methods:', error);
      setHighlyEffectiveMethods([]);
      setBarrierMethods([]);
    }
  };

  const fetchContraceptiveMethodOptions = async () => {
    try {
      const response = await fetchData('/contraceptive_method/all', 'GET', true);
      if (response?.data) {
        const methods = response.data.data || response.data;
        const methodsArray = Array.isArray(methods) ? methods : [];

        // Filter by category and map to {value: pk, label: reason}
        const highlyEffective = methodsArray
          .filter(method => method.category === 'highly_effective')
          .map(method => ({ value: method.pk, label: method.reason }));

        const barrier = methodsArray
          .filter(method => method.category === 'barrier')
          .map(method => ({ value: method.pk, label: method.reason }));

        setHighlyEffectiveMethodOptions(highlyEffective);
        setBarrierMethodOptions(barrier);
      }
    } catch (error) {
      console.error('Error fetching contraceptive method options:', error);
    }
  };

  const fetchSurplusCheck = async (patientPk) => {
    try {
      const response = await fetchData(`/prescription/surplus-check/${patientPk}`, 'GET', true);
      const data = response?.data?.data || response?.data || null;
      if (data && data.has_surplus) {
        setSurplusInfo(data);
      } else {
        setSurplusInfo(null);
      }
    } catch (error) {
      console.error('Error checking surplus dispensation:', error);
      setSurplusInfo(null);
    }
  };

  const processSelectedPatient = (selectedPatient) => {
    if (selectedPatient) {
      setPatient(selectedPatient.pk);
      setPatientData(selectedPatient);
      const hasPotential = normalizeGender(selectedPatient.gender) === getGenderEnum().F.value && selectedPatient.has_pregnancy_potential;
      setIsFemaleWithPotential(hasPotential);
      setLegalGuardianApproval(false);

      // Fetch last pregnancy test and contraceptive methods if patient has pregnancy potential
      if (hasPotential) {
        fetchLastPregnancyTest(selectedPatient.pk);
        fetchPatientContraceptiveMethods(selectedPatient.pk);
      } else {
        setPreviousPregnancyTestDate(null);
        setHighlyEffectiveMethods([]);
        setBarrierMethods([]);
      }

      // Fetch surplus dispensation info for prescription blocking rules
      fetchSurplusCheck(selectedPatient.pk);

      // Pre-fill menstrual cycle from patient data if available
      if (selectedPatient.menstrual_cycle_regular !== null && selectedPatient.menstrual_cycle_regular !== undefined) {
        setMenstrualCycle(selectedPatient.menstrual_cycle_regular ? MENSTRUAL_CYCLE_STATUS.REGULAR : MENSTRUAL_CYCLE_STATUS.IRREGULAR);
      } else {
        setMenstrualCycle('');
      }

      if (selectedPatient.prescription && selectedPatient.prescription.length > 0) {
        // Filter prescriptions (exclude CANCELED, include all other statuses)
        const validPrescriptions = selectedPatient.prescription.filter(p =>
          p.status !== PRESCRIPTION_STATUS.CANCELED
        );

        if (validPrescriptions.length > 0) {
          // Sort by prescription_date to get the most recent prescription
          const mostRecentPrescription = validPrescriptions.sort((a, b) =>
            new Date(b.prescription_date) - new Date(a.prescription_date)
          )[0];

          const dispensed = mostRecentPrescription.status === PRESCRIPTION_STATUS.DISPENSED;
          const active = mostRecentPrescription.status === PRESCRIPTION_STATUS.ACTIVE;
          const prescriptionExpired = isExpired(mostRecentPrescription.expiration_date);

          let shouldBlock = false;

          // Case 1: Active prescription that is not expired
          if (active && !prescriptionExpired) {
            shouldBlock = true;
            const expirationDate = new Date(mostRecentPrescription.expiration_date);
            const today = new Date();
            const daysRemaining = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
            setDaysRemainingForNewPrescription(daysRemaining);
            setNextPrescriptionDate(expirationDate);
          }
          // Case 2: Dispensed prescription within 19 days
          else if (dispensed) {
            const prescriptionDate = new Date(mostRecentPrescription.prescription_date);
            const minNewPrescriptionDate = new Date(prescriptionDate);
            minNewPrescriptionDate.setDate(minNewPrescriptionDate.getDate() + 19);
            const todayDate = new Date();
            todayDate.setHours(0, 0, 0, 0);
            minNewPrescriptionDate.setHours(0, 0, 0, 0);

            if (todayDate <= minNewPrescriptionDate) {
              shouldBlock = true;
              const daysRemaining = Math.ceil((minNewPrescriptionDate - todayDate) / (1000 * 60 * 60 * 24));
              setDaysRemainingForNewPrescription(daysRemaining);
              setNextPrescriptionDate(minNewPrescriptionDate);
            } else {
              setDaysRemainingForNewPrescription(0);
              setNextPrescriptionDate(null);
            }
          } else {
            setDaysRemainingForNewPrescription(0);
            setNextPrescriptionDate(null);
          }

          // Detect expired not-dispensed prescription - block form and show modal
          const expiredNotDispensed = active && prescriptionExpired;
          setHasExpiredNotDispensed(expiredNotDispensed);
          if (expiredNotDispensed) {
            shouldBlock = true;
            setActivePrescriptionInfo({
              prescription: {
                pk: mostRecentPrescription.pk,
                prescription_identifier: mostRecentPrescription.prescription_identifier,
                prescription_date: mostRecentPrescription.prescription_date,
                expiration_date: mostRecentPrescription.expiration_date,
                status: PRESCRIPTION_STATUS.ACTIVE,
              },
              isExpiredNotDispensed: true,
              errorMessage: intl.get("active_expired_not_dispensed_prescription").d(
                "Active and expired prescription (not dispensed). Please provide a justification for non-dispensation."
              ),
            });
            setShowActivePrescriptionModal(true);
          }

          setHasActivePrescription(active || dispensed);
          setActivePrescriptionPk(mostRecentPrescription.pk);
          setIsDispensed(dispensed);
          setPrescriptionExpired(prescriptionExpired);
          setBlockPrescription(shouldBlock);
        } else {
          // No valid prescriptions found
          setHasActivePrescription(false);
          setActivePrescriptionPk(null);
          setIsDispensed(false);
          setPrescriptionExpired(false);
          setBlockPrescription(false);
          setHasExpiredNotDispensed(false);
          setDaysRemainingForNewPrescription(0);
          setNextPrescriptionDate(null);
        }
      } else {
        setHasActivePrescription(false);
        setActivePrescriptionPk(null);
        setIsDispensed(false);
        setPrescriptionExpired(false);
        setBlockPrescription(false);
        setHasExpiredNotDispensed(false);
        setDaysRemainingForNewPrescription(0);
        setNextPrescriptionDate(null);
      }
    } else {
      setPatient('');
      setPatientData(null);
      setIsFemaleWithPotential(false);
      setHasActivePrescription(false);
      setActivePrescriptionPk(null);
      setIsDispensed(false);
      setPrescriptionExpired(false);
      setBlockPrescription(false);
      setHasExpiredNotDispensed(false);
      setDaysRemainingForNewPrescription(0);
      setNextPrescriptionDate(null);
      setLegalGuardianApproval(false);
      setHighlyEffectiveMethods([]);
      setBarrierMethods([]);
      setMedicalRecordsConfirmed(false);
      setFieldErrors({});
      setPreviousPregnancyTestDate(null);
      setSurplusInfo(null);
    }
  };

  const handleFindPatient = async () => {
    if (!cpf || !dob) {
      setErrorModalMessage(intl.get("missing_fields").d("Please enter CPF and Date of Birth."));
      setShowErrorModal(true);
      return;
    }
    try {
      setPatientAdded(false);
      setAddPatientError(null);
      const cleanedCpf = cpf.replace(/\D/g, '');
      const formattedDob = dob.format('YYYY-MM-DD');
      const response = await fetchData(`/patient/find_by_cpf_dob?cpf=${cleanedCpf}&dob=${formattedDob}`, 'GET', true);

      if (response.status === 200) {
        let foundPatient = response.data.data || response.data || response;
        if (foundPatient && foundPatient.pk) {
          // Fetch prescriptions for the found patient to check blocking rule
          try {
            const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${foundPatient.pk}?show_all=true`, 'GET', true);
            if (prescriptionsResponse.status === 200) {
              const prescriptions = prescriptionsResponse.data?.data || [];
              // Add prescriptions to patient data
              foundPatient = {
                ...foundPatient,
                prescription: prescriptions
              };
            }
          } catch (error) {
            console.error('Error fetching patient prescriptions:', error);
            // Continue with patient data even if prescription fetch fails
          }
          processSelectedPatient(foundPatient);
        } else {
          processSelectedPatient(null);
          setShowPatientNotFoundModal(true);
        }
      } else {
        processSelectedPatient(null);
        setShowPatientNotFoundModal(true);
      }
    } catch (error) {
      console.error('Error finding patient:', error);
      processSelectedPatient(null);
      setShowPatientNotFoundModal(true);
    }
  };

  const handlePatientChange = async (value) => {
    const selectedPatientId = value;
    let selectedPatient = (patients || []).find(p => p.pk === selectedPatientId);

    // Fetch prescriptions for the selected patient to check blocking rule
    if (selectedPatient && selectedPatient.pk) {
      try {
        const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${selectedPatient.pk}?show_all=true`, 'GET', true);
        if (prescriptionsResponse.status === 200) {
          const prescriptions = prescriptionsResponse.data?.data || [];
          // Add prescriptions to patient data
          selectedPatient = {
            ...selectedPatient,
            prescription: prescriptions
          };
        }
      } catch (error) {
        console.error('Error fetching patient prescriptions:', error);
        // Continue with patient data even if prescription fetch fails
      }
    }

    processSelectedPatient(selectedPatient);
  };

  const handleFinalizePrescription = async () => {
    // Clear previous errors
    setFieldErrors({});
    const errors = {};

    // Validate required fields
    if (!patient) {
      errors.patient = intl.get("select_patient_first").d("Please select a patient first.");
    }

    if (!selectedPosology) {
      errors.posology = intl.get("select_posology_first").d("Please select a posology.");
    }

    if (!institution) {
      errors.institution = intl.get("select_institution_first").d("Please select an institution.");
    }

    if (isFemaleWithPotential) {
      if (!lastPregnancyTestDate) {
        errors.pregnancyTestDate = intl.get("enter_pregnancy_test_date").d("Please enter the pregnancy test date.");
      }
      if (!pregnancyTestConfirmed) {
        errors.pregnancyTestConfirmed = intl.get("confirm_pregnancy_test").d("Please confirm the negative pregnancy test.");
      }
      if (highlyEffectiveMethods.length === 0) {
        errors.highlyEffectiveMethods = intl.get("select_highly_effective_method").d("Please select at least one highly effective contraceptive method.");
      }
      if (barrierMethods.length === 0) {
        errors.barrierMethods = intl.get("select_barrier_method").d("Please select at least one barrier contraceptive method.");
      }
      if (!medicalRecordsConfirmed) {
        errors.medicalRecordsConfirmed = intl.get("confirm_medical_records").d("Please confirm that the medical records documentation will be archived.");
      }
    }

    if (!indication) {
      errors.indication = intl.get("select_indication_first").d("Please select an indication.");
    }

    if (indication === IndicationEnum.OTHER && !indicationOther) {
      errors.indicationOther = intl.get("enter_other_indication").d("Please specify the other indication.");
    }

    if (!prescriptionDate) {
      errors.prescriptionDate = intl.get("enter_prescription_date").d("Please enter the prescription date.");
    }

    // If there are errors, set them and show the first error message
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors)[0];
      setErrorModalMessage(firstError);
      setShowErrorModal(true);
      return;
    }

    // Check if patient has a legal guardian and if approval is required
    if (patientData?.responsible_legal && patientData.responsible_legal.length > 0 && !legalGuardianApproval) {
      setShowLegalGuardianModal(true);
      return;
    }

    // Validate expiration date
    if (!expirationDate) {
      setErrorModalMessage(intl.get("expiration_date_not_calculated").d("Expiration date could not be calculated. Please check the dates."));
      setShowErrorModal(true);
      return;
    }

    // Validate pregnancy test date for female with potential
    let pregnancyTestDate = null;
    if (isFemaleWithPotential) {
      if (!lastPregnancyTestDate) {
        setErrorModalMessage(intl.get("enter_pregnancy_test_date").d("Please enter the pregnancy test date."));
        setShowErrorModal(true);
        return;
      }
      pregnancyTestDate = lastPregnancyTestDate;
    }

    // Determine dosage description and structured fields based on selection
    let finalDosageDescription = null;
    let finalDosageQuantity = null;
    let finalDosageDuration = null;
    let finalDosagePauseDays = null;
    let finalMedicationPk = null;
    let finalPosologyPk = null;
    let finalDosageNotes = null;

    if (selectedPosology) {
      // Find the posology and use its static description
      const selectedPosologyData = posologies.find(p => p.pk === selectedPosology);
      if (selectedPosologyData) {
        // PRIORITY 1: Use static description if present
        if (selectedPosologyData.description) {
          finalDosageDescription = selectedPosologyData.description;
        } else {
          // PRIORITY 2: Fallback to dynamic generation for backward compatibility
          const parts = selectedPosologyData.parts || [];
          const doseString = parts.length > 0
            ? parts.map(part => {
              const qty = part.quantity || 1;
              const capsuleWord = qty > 1 ? 'CÁPSULAS' : 'CÁPSULA';
              const qtyInWords = numberToPortugueseWords(qty).toUpperCase();
              const partName = (part.name || part.description || '').toUpperCase();
              return `${qty} (${qtyInWords}) ${capsuleWord} DE ${partName}`;
            }).join(' E ')
            : '';

          let cycleString = '';
          if (selectedPosologyData.duration) {
            const treatmentDays = selectedPosologyData.duration - (selectedPosologyData.pause_days || 0);
            if (selectedPosologyData.pause_days && selectedPosologyData.pause_days > 0) {
              cycleString = `POR ${treatmentDays} DIAS E REALIZAR PAUSA POR ${selectedPosologyData.pause_days} DIAS (CICLO DE ${selectedPosologyData.duration} DIAS)`;
            } else {
              cycleString = `POR ${selectedPosologyData.duration} DIAS, SEM PAUSAS (CICLO DE ${selectedPosologyData.duration} DIAS)`;
            }
          }

          finalDosageDescription = doseString && cycleString ? `ADMINISTRAR ${doseString} ${cycleString}` : doseString || cycleString || null;
        }

        // Copy structured data from predefined posology
        const parts = selectedPosologyData.parts || [];
        if (parts.length > 0) {
          finalDosageQuantity = parts[0].quantity || 1;
        }
        finalDosageDuration = selectedPosologyData.duration || null;
        finalDosagePauseDays = selectedPosologyData.pause_days ?? null;
        finalPosologyPk = selectedPosologyData.pk;
        // Use medication_pk from the posology if available
        finalMedicationPk = selectedPosologyData.medication_pk || null;
        // Include additional notes even for predefined posology
        finalDosageNotes = dosageNotes || null;
      }
    }

    // Build prescription data
    const prescriptionData = {
      patient_pk: patient,
      doctor_pk: doctorInfo?.doctor_pk || null,
      doctor_crm_pk: selectedCrm?.pk ?? null,
      organization_branch_pk: institution || null,
      prescription_date: prescriptionDate.toISOString(),
      expiration_date: expirationDate.toISOString(),
      pregnancy_test_date: pregnancyTestDate ? pregnancyTestDate.toISOString() : null,
      dosage_description: finalDosageDescription,
      // Structured posology fields
      dosage_quantity: finalDosageQuantity,
      dosage_duration: finalDosageDuration,
      medication_pk: finalMedicationPk,
      posology_pk: finalPosologyPk,
      dosage_notes: finalDosageNotes,
      dosage_pause_days: finalDosagePauseDays,
      indication: indication ? parseInt(indication) : null,
      indication_other: indication === IndicationEnum.OTHER ? indicationOther : null,
      status: 1,
      liability_waiver_signed: liabilityWaiverSigned,
      cancellation_justification: justification || null,
      allow_alternative_dispensation: allowAlternativeDispensation,
      dosage_rectification_term_accepted: allowAlternativeDispensation ? dosageRectificationTermAccepted : false,
      highly_effective_methods: isFemaleWithPotential ? highlyEffectiveMethods : null,
      barrier_methods: isFemaleWithPotential ? barrierMethods : null,
      menstrual_cycle_regular: isFemaleWithPotential
        ? menstrualCycle === MENSTRUAL_CYCLE_STATUS.REGULAR
        : null,
    };

    // Store prescription data for preview
    setPreviewPrescriptionData(prescriptionData);

    // TODO: Check if quantity is above package insert and show alert modal
    // For now, we'll skip the quantity check and go directly to preview
    // In the future, you can add logic here to check if quantity exceeds threshold
    // and show the quantity alert modal first

    // Show preview modal
    setShowPreviewModal(true);
  };

  const handleConfirmPrescription = async () => {
    if (!previewPrescriptionData) {
      return;
    }

    // Start loading state
    setCreatingPrescription(true);

    try {
      // Add above_package_insert_confirmed if applicable
      const finalPrescriptionData = {
        ...previewPrescriptionData,
        above_package_insert_confirmed: abovePackageInsertConfirmed,
      };

      const response = await fetchData('/prescription', 'POST', true, finalPrescriptionData);

      // Check for 403 error - training not completed or institution inactive
      if (response.status === 403) {
        setCreatingPrescription(false);
        setShowPreviewModal(false);
        const errorDetail = response.data?.detail;
        setErrorModalMessage(
          errorDetail ||
          intl.get("training_required_for_prescription").d(
            "You must complete the required training to have full access to the system. Please complete the 'Prescrição e Dispensação' training before creating prescriptions."
          )
        );
        setShowErrorModal(true);
        return;
      }

      // Check for 400 error even if fetchData doesn't throw
      if (response.status === 400) {
        const errorDetail = response.data?.detail || response.data?.message || '';

        // Check for expired not-dispensed prescription (structured error with type field)
        if (typeof errorDetail === 'object' && errorDetail.type === 'expired_not_dispensed') {
          setCreatingPrescription(false);
          setShowPreviewModal(false);
          setActivePrescriptionInfo({
            prescription: {
              pk: errorDetail.prescription_pk,
              prescription_identifier: errorDetail.prescription_identifier,
              prescription_date: errorDetail.prescription_date,
              expiration_date: errorDetail.expiration_date,
              status: PRESCRIPTION_STATUS.ACTIVE,
            },
            isExpiredNotDispensed: true,
            errorMessage: errorDetail.message,
          });
          setShowActivePrescriptionModal(true);
          return;
        }

        // Check for both old and new error messages for compatibility
        if (typeof errorDetail === 'string' &&
          (errorDetail.includes('Patient has an active prescription that is still valid') ||
            errorDetail.includes('Patient already has a recent prescription'))) {

          // Find the most recent prescription (excluding CANCELED)
          const validPrescriptions = patientData?.prescription?.filter(p =>
            p.status !== PRESCRIPTION_STATUS.CANCELED
          ) || [];

          const recentPrescription = validPrescriptions.sort((a, b) =>
            new Date(b.created_on) - new Date(a.created_on)
          )[0];

          setCreatingPrescription(false);
          setShowPreviewModal(false);
          setActivePrescriptionInfo({
            prescription: recentPrescription,
            canCancel: recentPrescription?.doctor_pk === doctorInfo?.doctor_pk && recentPrescription?.status === PRESCRIPTION_STATUS.ACTIVE,
            errorMessage: errorDetail
          });
          setShowActivePrescriptionModal(true);
          return;
        }
      }

      if (response.status === HTTP_STATUS.CREATED_201) {
        const createdPrescription = response.data.data || response.data;
        const prescriptionPk = createdPrescription.pk;

        // Close preview modal and stop loading
        setCreatingPrescription(false);
        setShowPreviewModal(false);

        setCreatedPrescriptionPk(prescriptionPk);
        setShowSuccessModal(true);

        // Reset form
        setPatient('');
        setPatientData(null);
        setCpf('');
        setDob(null);
        setSelectedPosology('');
        // Don't reset institution if it was auto-selected
        if (showInstitutionSelect) {
          setInstitution('');
        }
        setJustification('');
        setLiabilityWaiverSigned(false);
        setPregnancyTestConfirmed(false);
        setLastPregnancyTestDate(null);
        setPrescriptionDate(dayjs());
        setExpirationDate(null);
        setMenstrualCycle('');
        setIsFemaleWithPotential(false);
        setHasActivePrescription(false);
        setIsDispensed(false);
        setPrescriptionExpired(false);
        setBlockPrescription(false);
        setDaysRemainingForNewPrescription(0);
        setNextPrescriptionDate(null);
        setLegalGuardianApproval(false);
        setAllowAlternativeDispensation(false);
        setPreviewPrescriptionData(null);
        setAbovePackageInsertConfirmed(false);
        setHighlyEffectiveMethods([]);
        setBarrierMethods([]);
        setMedicalRecordsConfirmed(false);
        setFieldErrors({});

        if (onSuccess) {
          // Clear any existing timeout
          if (successModalTimeoutRef.current) {
            clearTimeout(successModalTimeoutRef.current);
          }
          // Auto-close after 10 seconds, but user can close manually before
          successModalTimeoutRef.current = setTimeout(() => {
            onSuccess();
          }, 10000);
        }
      } else {
        setCreatingPrescription(false);
        setShowPreviewModal(false);
        // Show backend error detail if available
        const errorDetail = response.data?.detail || response.data?.message;
        const errorMessage = typeof errorDetail === 'string' ? errorDetail :
          typeof errorDetail === 'object' ? JSON.stringify(errorDetail) :
            intl.get("prescription_creation_failed").d("Failed to create prescription. Please try again.");
        setErrorModalMessage(errorMessage);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error creating prescription:', error);
      console.error('Error response:', error.response);

      if (error.response && error.response.status === 400) {
        const errorDetail = error.response.data?.detail || error.response.data?.message || '';

        // Check for expired not-dispensed prescription (structured error with type field)
        if (typeof errorDetail === 'object' && errorDetail.type === 'expired_not_dispensed') {
          setCreatingPrescription(false);
          setShowPreviewModal(false);
          setActivePrescriptionInfo({
            prescription: {
              pk: errorDetail.prescription_pk,
              prescription_identifier: errorDetail.prescription_identifier,
              prescription_date: errorDetail.prescription_date,
              expiration_date: errorDetail.expiration_date,
              status: PRESCRIPTION_STATUS.ACTIVE,
            },
            isExpiredNotDispensed: true,
            errorMessage: errorDetail.message,
          });
          setShowActivePrescriptionModal(true);
          return;
        }

        // Check for both old and new error messages for compatibility
        if (typeof errorDetail === 'string' &&
          (errorDetail.includes('Patient has an active prescription that is still valid') ||
            errorDetail.includes('Patient already has a recent prescription'))) {

          // Find the most recent prescription (excluding CANCELED)
          let recentPrescription = patientData?.prescription?.filter(p =>
            p.status !== PRESCRIPTION_STATUS.CANCELED
          ).sort((a, b) => new Date(b.created_on) - new Date(a.created_on))[0];

          // If prescription not found in patientData, fetch from backend
          if (!recentPrescription && patientData?.pk) {
            try {
              const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${patientData.pk}`, 'GET', true);
              if (prescriptionsResponse.status === 200) {
                const prescriptions = prescriptionsResponse.data?.data || [];
                const validPrescriptions = prescriptions.filter(p =>
                  p.status !== PRESCRIPTION_STATUS.CANCELED
                );
                recentPrescription = validPrescriptions.sort((a, b) =>
                  new Date(b.created_on) - new Date(a.created_on)
                )[0];
              }
            } catch (fetchError) {
              console.error('Error fetching patient prescriptions:', fetchError);
            }
          }

          const canCancel = Number(recentPrescription?.doctor_pk) === Number(doctorInfo?.doctor_pk) &&
            recentPrescription?.status === PRESCRIPTION_STATUS.ACTIVE;

          setCreatingPrescription(false);
          setShowPreviewModal(false);
          setActivePrescriptionInfo({
            prescription: recentPrescription,
            canCancel: canCancel,
            errorMessage: errorDetail
          });
          setActivePrescriptionPk(recentPrescription?.pk);
          setShowActivePrescriptionModal(true);
          return;
        }
      }

      setCreatingPrescription(false);
      setShowPreviewModal(false);
      // Show backend error detail if available
      const errorDetail = error.response?.data?.detail || error.response?.data?.message;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail :
        typeof errorDetail === 'object' ? JSON.stringify(errorDetail) :
          intl.get("prescription_creation_error").d("Error creating prescription. Please try again.");
      setErrorModalMessage(errorMessage);
      setShowErrorModal(true);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCancelActivePrescription = async () => {
    if (!activePrescriptionPk) {
      setErrorModalMessage(intl.get("no_active_prescription_to_cancel").d("No active prescription to cancel."));
      setShowErrorModal(true);
      return;
    }

    if (!justification || justification.trim().length === 0) {
      setErrorModalMessage(intl.get("cancellation_justification_required").d("Cancellation justification is required."));
      setShowErrorModal(true);
      return;
    }

    try {
      const response = await fetchData('/prescription/cancel', 'POST', true, {
        prescription_pk: activePrescriptionPk,
        cancellation_justification: justification
      });

      if (response.status === HTTP_STATUS.OK_200) {
        setErrorModalMessage(intl.get("prescription_canceled_success").d("Prescription canceled successfully! You can now create a new prescription."));
        setShowErrorModal(true);

        if (useCpfSearch && cpf && dob) {
          const cleanedCpf = cpf.replace(/\D/g, '');
          const formattedDob = dob.format('YYYY-MM-DD');
          const patientResponse = await fetchData(`/patient/find_by_cpf_dob?cpf=${cleanedCpf}&dob=${formattedDob}`, 'GET', true);
          if (patientResponse.status === 200) {
            let foundPatient = patientResponse.data.data || patientResponse.data || patientResponse;
            if (foundPatient && foundPatient.pk) {
              // Fetch prescriptions after cancellation to refresh blocking status
              try {
                const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${foundPatient.pk}?show_all=true`, 'GET', true);
                if (prescriptionsResponse.status === 200) {
                  const prescriptions = prescriptionsResponse.data?.data || [];
                  foundPatient = {
                    ...foundPatient,
                    prescription: prescriptions
                  };
                }
              } catch (error) {
                console.error('Error fetching patient prescriptions:', error);
              }
              processSelectedPatient(foundPatient);
            }
          }
        } else {
          const patientsResponse = await fetchData('/doctor/all_patients', 'GET', true);
          if (patientsResponse.status === 200) {
            const patientsData = patientsResponse.data.data?.rows || patientsResponse.data.data || [];
            setPatients(patientsData);
            const updatedPatient = patientsData.find(p => p.pk === patient);
            if (updatedPatient) {
              handlePatientChange(updatedPatient.pk);
            }
          }
        }

        setJustification('');
      } else {
        setErrorModalMessage(intl.get("prescription_cancel_failed").d("Failed to cancel prescription. Please try again."));
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error canceling prescription:', error);
      setErrorModalMessage(intl.get("prescription_cancel_error").d("Error canceling prescription. Please try again."));
      setShowErrorModal(true);
    }
  };

  // Sort posologies by dose strength (smallest MG first)
  const getDoseMg = (posology) => {
    // Try extracting from parts first
    const parts = posology.parts || [];
    for (const part of parts) {
      const partName = (part.name || '').toUpperCase();
      const match = partName.match(/(\d+)\s*MG/i);
      if (match) return parseInt(match[1], 10);
    }
    // Fallback: extract from description
    const desc = (posology.description || '').toUpperCase();
    const match = desc.match(/(\d+)\s*MG/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  const sortedPosologies = [...posologies].sort((a, b) => getDoseMg(a) - getDoseMg(b));

  // Build options for DmsAutocomplete components
  const posologySelectOptions = [
    ...sortedPosologies.map(p => {
      // PRIORITY 1: Use static description if present (from database fixture)
      if (p.description) {
        return { value: p.pk, label: p.description };
      }

      // PRIORITY 2: Fallback to dynamic generation for backward compatibility
      const parts = p.parts || [];
      const doseString = parts.length > 0
        ? parts.map(part => {
          const qty = part.quantity || 1;
          const capsuleWord = qty > 1 ? 'CÁPSULAS' : 'CÁPSULA';
          const qtyInWords = numberToPortugueseWords(qty).toUpperCase();
          const partName = (part.name || part.description || '').toUpperCase();
          return `${qty} (${qtyInWords}) ${capsuleWord} DE ${partName}`;
        }).join(' E ')
        : '';

      let cycleString = '';
      if (p.duration) {
        const treatmentDays = p.duration - (p.pause_days || 0);
        if (p.pause_days && p.pause_days > 0) {
          cycleString = `POR ${treatmentDays} DIAS E REALIZAR PAUSA POR ${p.pause_days} DIAS (CICLO DE ${p.duration} DIAS)`;
        } else {
          cycleString = `POR ${p.duration} DIAS, SEM PAUSAS (CICLO DE ${p.duration} DIAS)`;
        }
      }

      const label = doseString && cycleString ? `ADMINISTRAR ${doseString} ${cycleString}` : doseString || cycleString || `Posologia ${p.pk}`;
      return { value: p.pk, label };
    }),
  ];


  const surplusValidation = useMemo(() => {
    if (!surplusInfo || !selectedPosology) {
      return { blocked: false, capsuleReturnWarning: false, message: '' };
    }

    const blockUntilStr = surplusInfo.block_same_posology_until.split('T')[0];
    const todayStr = new Date().toISOString().split('T')[0];

    // D+26 já passou → paciente não tem mais cápsulas restantes
    if (todayStr > blockUntilStr) {
      return { blocked: false, capsuleReturnWarning: false, message: '' };
    }

    const isSamePosology = selectedPosology === surplusInfo.surplus_posology_pk;

    if (isSamePosology) {
      const blockUntil = new Date(blockUntilStr + 'T00:00:00');
      const today = new Date(todayStr + 'T00:00:00');
      const daysRemaining = Math.ceil((blockUntil - today) / (1000 * 60 * 60 * 24));
      const dateStr = blockUntil.toLocaleDateString('pt-BR');
      return {
        blocked: true,
        capsuleReturnWarning: false,
        message: intl.get("Prescription.SurplusBlockSamePosology").d(
          `Patient has surplus capsules from last dispensation with the same posology. Next prescription can be created in ${daysRemaining} days (on ${dateStr}).`
        ),
      };
    }

    return {
      blocked: false,
      capsuleReturnWarning: true,
      message: intl.get("Prescription.SurplusCapsuleReturn").d(
        "Patient has surplus capsules from the last dispensation. The patient must return the remaining capsules."
      ),
    };
  }, [surplusInfo, selectedPosology]);

  const indicationSelectOptions = useMemo(() => getIndicationOptions(), []);

  return (
    <>
      <DmsAltBox.Card sx={isModal ? { boxShadow: 'none' } : {}}>
        <DmsAltBox.Header>
          <Typography variant="h6">
            {intl.get("patient_selection").d("1. Patient Selection")}
          </Typography>
        </DmsAltBox.Header>
        <DmsAltBox.Body>
          {/* Patient already provided via initialPatient prop */}
          {initialPatient && patientData ? (
            <Box sx={{ mt: 0 }}>
              {/* Dados do Paciente - Pre-filled */}
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {intl.get("patient_data").d("Patient Data")}
                  </Typography>
                  <Tooltip title={intl.get("edit_patient").d("Edit Patient")}>
                    <IconButton
                      size="small"
                      onClick={handleOpenEditPatient}
                      sx={{ color: 'primary.main' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                      {intl.get("name").d("Name")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {patientData.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                      {intl.get("CPF").d("CPF")}
                    </Typography>
                    <Typography variant="body1">
                      {patientData.cpf}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                      {intl.get("gender").d("Gender")}
                    </Typography>
                    <Typography variant="body1">
                      {normalizeGender(patientData.gender) === getGenderEnum().M.value ? intl.get("male").d("Male") : intl.get("female").d("Female")}
                    </Typography>
                  </Grid>
                  {patientData.birth_date && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {intl.get("BirthDate").d("Birth Date")}
                      </Typography>
                      <Typography variant="body1">
                        {dayjs(patientData.birth_date).format('DD/MM/YYYY')}
                      </Typography>
                    </Grid>
                  )}
                  {patientData.rg && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {intl.get("rg").d("RG")}
                      </Typography>
                      <Typography variant="body1">
                        {patientData.rg}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Legal Guardian Information */}
              {patientData.responsible_legal && patientData.responsible_legal.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.main', mb: 2 }}>
                    {intl.get("ResponsibleLegal").d("Legal Guardian")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {intl.get("name").d("Name")}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {patientData.responsible_legal[0].name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {intl.get("CPF").d("CPF")}
                      </Typography>
                      <Typography variant="body1">
                        {patientData.responsible_legal[0].cpf}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          ) : useCpfSearch ? (
            <>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <DmsTextField
                    label={intl.get("CPF").d("CPF")}
                    name="cpf"
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                    fullWidth
                    placeholder="000.000.000-00"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DmsCalendar.DmsFlexiblePicker
                    pickerType="date"
                    label={intl.get("BirthDate").d("Birth Date")}
                    value={dob}
                    onChange={(newValue) => setDob(newValue)}
                    format="DD/MM/YYYY"
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'flex-end', pb: '4px' }}>
                  <DmsButton
                    text={intl.get("find").d("Find")}
                    onClick={handleFindPatient}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {patientData && (
                <Box sx={{ mt: 3 }}>
                  {/* Dados do Paciente */}
                  <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {intl.get("patient_data").d("Patient Data")}
                      </Typography>
                      <DmsButton
                        text={patientAdded ? intl.get("patient_added").d("Patient Added") : intl.get("add_to_my_list").d("Add to my list")}
                        onClick={handleAddPatientToDoctorList}
                        disabled={isAddingPatient || patientAdded}
                        variant={patientAdded ? "success" : "primary"}
                      />
                    </Box>
                    {addPatientError && <DmsAlertInfo sx={{ mb: 2 }} message={addPatientError} />}
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                          {intl.get("name").d("Name")}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {patientData.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                          {intl.get("CPF").d("CPF")}
                        </Typography>
                        <Typography variant="body1">
                          {patientData.cpf}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                          {intl.get("gender").d("Gender")}
                        </Typography>
                        <Typography variant="body1">
                          {normalizeGender(patientData.gender) === getGenderEnum().M.value ? intl.get("male").d("Male") : intl.get("female").d("Female")}
                        </Typography>
                      </Grid>
                      {patientData.rg && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                            {intl.get("rg").d("RG")}
                          </Typography>
                          <Typography variant="body1">
                            {patientData.rg}
                          </Typography>
                        </Grid>
                      )}
                      {patientData.rg_issuing_agency && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                            {intl.get("rg_issuing_agency").d("Issuing Agency")}
                          </Typography>
                          <Typography variant="body1">
                            {patientData.rg_issuing_agency}
                          </Typography>
                        </Grid>
                      )}
                      {patientData.birth_date && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                            {intl.get("BirthDate").d("Birth Date")}
                          </Typography>
                          <Typography variant="body1">
                            {dayjs(patientData.birth_date).format('DD/MM/YYYY')}
                          </Typography>
                        </Grid>
                      )}
                      {patientData.email && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                            {intl.get("email").d("Email")}
                          </Typography>
                          <Typography variant="body1">
                            {patientData.email}
                          </Typography>
                        </Grid>
                      )}
                      {patientData.phone && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                            {intl.get("phone").d("Phone")}
                          </Typography>
                          <Typography variant="body1">
                            {patientData.phone}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Legal Guardian Information */}
                  {patientData.responsible_legal && patientData.responsible_legal.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.main', mb: 2 }}>
                        {intl.get("ResponsibleLegal").d("Legal Guardian")}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                            {intl.get("name").d("Name")}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {patientData.responsible_legal[0].name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                            {intl.get("CPF").d("CPF")}
                          </Typography>
                          <Typography variant="body1">
                            {patientData.responsible_legal[0].cpf}
                          </Typography>
                        </Grid>
                        {patientData.responsible_legal[0].rg && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                              {intl.get("rg").d("RG")}
                            </Typography>
                            <Typography variant="body1">
                              {patientData.responsible_legal[0].rg}
                            </Typography>
                          </Grid>
                        )}
                        {patientData.responsible_legal[0].rg_issuing_agency && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                              {intl.get("rg_issuing_agency").d("Issuing Agency")}
                            </Typography>
                            <Typography variant="body1">
                              {patientData.responsible_legal[0].rg_issuing_agency}
                            </Typography>
                          </Grid>
                        )}
                        {patientData.responsible_legal[0].email && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                              {intl.get("email").d("Email")}
                            </Typography>
                            <Typography variant="body1">
                              {patientData.responsible_legal[0].email}
                            </Typography>
                          </Grid>
                        )}
                        {patientData.responsible_legal[0].phone && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                              {intl.get("phone").d("Phone")}
                            </Typography>
                            <Typography variant="body1">
                              {patientData.responsible_legal[0].phone}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <DmsSelect
              label={intl.get("Patient").d("Patient")}
              name="patient"
              value={patient}
              onChange={handlePatientChange}
              url="/doctor/all_patients"
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          {/* Prescription blocking alert removed - now shown in separate modal before opening form */}
        </DmsAltBox.Body>
      </DmsAltBox.Card>

      {patient && !blockPrescription && (
        <DmsAltBox.Card sx={isModal ? { mt: 3, boxShadow: 'none' } : { mt: 0 }}>
          <DmsAltBox.Header>
            <Typography variant="h6">
              {intl.get("PrescriptionData").d("Prescription Data")}
            </Typography>
          </DmsAltBox.Header>
          <DmsAltBox.Body>
            {/* Female Patient - Pregnancy Potential Section */}
            {normalizeGender(patientData?.gender) === getGenderEnum().F.value && (
              <Box sx={{ mb: 3, p: 2, bgcolor: isFemaleWithPotential ? 'warning.50' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: isFemaleWithPotential ? 'warning.200' : 'grey.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: isFemaleWithPotential ? 'warning.dark' : 'text.primary' }}>
                  {intl.get("MedicalInfo").d("Medical Information")}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                    ({intl.get("ValidateWithPatient").d("Validate with patient")})
                  </Typography>
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl component="fieldset" fullWidth>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        {intl.get("PregnancyPotential").d("Pregnancy Potential")} *
                      </Typography>
                      <RadioGroup
                        row
                        name="pregnancyPotential"
                        value={isFemaleWithPotential ? 'yes' : 'no'}
                        onChange={(e) => {
                          const newValue = e.target.value === 'yes';
                          const isChanging = isFemaleWithPotential !== newValue;

                          if (isChanging) {
                            // Trigger warning for any change (both Yes → No and No → Yes)
                            setShowPregnancyPotentialWarning(true);
                          }
                        }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio size="small" color="warning" />}
                          label={<Typography variant="body2">{intl.get("Yes").d("Yes")}</Typography>}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio size="small" color="success" />}
                          label={<Typography variant="body2">{intl.get("No").d("No")}</Typography>}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {isFemaleWithPotential && (
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" fullWidth error={!!fieldErrors.menstrualCycle}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: fieldErrors.menstrualCycle ? 'error.main' : 'inherit' }}>
                          {intl.get("MenstrualCycle").d("Menstrual Cycle")} *
                        </Typography>
                        <RadioGroup
                          row
                          name="menstrualCycle"
                          value={menstrualCycle}
                          onChange={(e) => {
                            setMenstrualCycle(e.target.value);
                            if (fieldErrors.menstrualCycle) setFieldErrors(prev => ({ ...prev, menstrualCycle: null }));
                          }}
                        >
                          <FormControlLabel
                            value={MENSTRUAL_CYCLE_STATUS.REGULAR}
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">{intl.get("Regular").d("Regular")}</Typography>}
                          />
                          <FormControlLabel
                            value={MENSTRUAL_CYCLE_STATUS.IRREGULAR}
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">{intl.get("Irregular").d("Irregular")}</Typography>}
                          />
                        </RadioGroup>
                        {fieldErrors.menstrualCycle && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {fieldErrors.menstrualCycle}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {isFemaleWithPotential ? (
              <Box sx={{ mb: 2 }}>
                {/* Pregnancy Test Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                    {intl.get("PregnancyTestInfo").d("Pregnancy Test Information")}
                  </Typography>

                  {menstrualCycle === MENSTRUAL_CYCLE_STATUS.IRREGULAR && (
                    <DmsAlertWarning sx={{ mb: 2 }} message={intl.get("IrregularCycleWarning").d("Attention: Repeat pregnancy test every 15 days (2 weeks).")} />
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <DmsCalendar.DmsFlexiblePicker
                        pickerType="date"
                        label={`${intl.get("LastPregnancyTestDate").d("Last Pregnancy Test Date")} *`}
                        value={lastPregnancyTestDate}
                        onChange={(newValue) => {
                          // Validate date - if invalid, default to today
                          const validDate = newValue && dayjs(newValue).isValid() ? newValue : dayjs();
                          setLastPregnancyTestDate(validDate);
                          if (fieldErrors.pregnancyTestDate) setFieldErrors(prev => ({ ...prev, pregnancyTestDate: null }));
                        }}
                        format="DD/MM/YYYY"
                        minDate={dayjs().subtract(6, 'day')}
                        maxDate={dayjs()}
                        sx={{ width: "100%", minWidth: "320px" }}
                        slotProps={{
                          textField: {
                            error: !!fieldErrors.pregnancyTestDate,
                            helperText: fieldErrors.pregnancyTestDate,
                            inputProps: { readOnly: true },
                            sx: { cursor: 'pointer', minWidth: "320px" }
                          }
                        }}
                      />
                    </Grid>
                    {previousPregnancyTestDate && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {intl.get("LastTestFromPreviousPrescription").d("Last test (previous prescription)")}: {' '}
                            <strong>{previousPregnancyTestDate.format('DD/MM/YYYY')}</strong>
                          </Typography>
                          {dayjs().diff(previousPregnancyTestDate, 'day') > 14 && (
                            <DmsAlertWarning
                              sx={{ mt: 1 }}
                              message={intl.get("PregnancyTestOlderThan14Days").d("The last pregnancy test was performed more than 14 days ago.")}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  <Box sx={{
                    p: fieldErrors.pregnancyTestConfirmed ? 1.5 : 0,
                    borderRadius: 1,
                    border: fieldErrors.pregnancyTestConfirmed ? '1px solid' : 'none',
                    borderColor: 'error.main',
                    bgcolor: fieldErrors.pregnancyTestConfirmed ? 'error.50' : 'transparent'
                  }}>
                    <DmsCheckBox
                      label={`${intl.get("ConfirmNegativePregnancyTest").d("I confirm the test was performed with a negative result.")} *`}
                      labelComponent={(props) => <Typography variant="body2" {...props} />}
                      checked={pregnancyTestConfirmed}
                      onChange={(e) => {
                        setPregnancyTestConfirmed(e.target.checked);
                        if (fieldErrors.pregnancyTestConfirmed) setFieldErrors(prev => ({ ...prev, pregnancyTestConfirmed: null }));
                      }}
                      name="pregnancyTestConfirmed"
                    />
                    {fieldErrors.pregnancyTestConfirmed && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {fieldErrors.pregnancyTestConfirmed}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Contraceptive Methods Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    {intl.get("ContraceptiveMethodsRequired").d("Contraceptive Methods (Required)")} *
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {intl.get("ContraceptiveMethodsInstruction").d("Select at least one highly effective method and one barrier method that the patient is using.")}
                  </Typography>

                  <Grid container spacing={3}>
                    {/* Highly Effective Methods */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        p: 2,
                        bgcolor: fieldErrors.highlyEffectiveMethods ? 'error.50' : 'grey.50',
                        borderRadius: 1,
                        height: '100%',
                        border: fieldErrors.highlyEffectiveMethods ? '1px solid' : 'none',
                        borderColor: 'error.main'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: fieldErrors.highlyEffectiveMethods ? 'error.main' : 'success.dark' }}>
                          {intl.get("HighlyEffectiveMethods").d("Highly Effective Method")} *
                        </Typography>
                        <FormGroup>
                          {highlyEffectiveMethodOptions.map((method) => (
                            <FormControlLabel
                              key={method.value}
                              control={
                                <Checkbox
                                  checked={highlyEffectiveMethods.includes(method.value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setHighlyEffectiveMethods([...highlyEffectiveMethods, method.value]);
                                    } else {
                                      setHighlyEffectiveMethods(highlyEffectiveMethods.filter(m => m !== method.value));
                                    }
                                    if (fieldErrors.highlyEffectiveMethods) setFieldErrors(prev => ({ ...prev, highlyEffectiveMethods: null }));
                                  }}
                                  size="small"
                                  color="success"
                                />
                              }
                              label={<Typography variant="body2">{method.label}</Typography>}
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </FormGroup>
                        {fieldErrors.highlyEffectiveMethods && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                            {fieldErrors.highlyEffectiveMethods}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Barrier Methods */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        p: 2,
                        bgcolor: fieldErrors.barrierMethods ? 'error.50' : 'grey.50',
                        borderRadius: 1,
                        height: '100%',
                        border: fieldErrors.barrierMethods ? '1px solid' : 'none',
                        borderColor: 'error.main'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: fieldErrors.barrierMethods ? 'error.main' : 'warning.dark' }}>
                          {intl.get("BarrierMethods").d("Barrier Method")} *
                        </Typography>
                        <FormGroup>
                          {barrierMethodOptions.map((method) => (
                            <FormControlLabel
                              key={method.value}
                              control={
                                <Checkbox
                                  checked={barrierMethods.includes(method.value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setBarrierMethods([...barrierMethods, method.value]);
                                    } else {
                                      setBarrierMethods(barrierMethods.filter(m => m !== method.value));
                                    }
                                    if (fieldErrors.barrierMethods) setFieldErrors(prev => ({ ...prev, barrierMethods: null }));
                                  }}
                                  size="small"
                                  color="warning"
                                />
                              }
                              label={<Typography variant="body2">{method.label}</Typography>}
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </FormGroup>
                        {fieldErrors.barrierMethods && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                            {fieldErrors.barrierMethods}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Medical Records Confirmation */}
                <Box sx={{
                  p: fieldErrors.medicalRecordsConfirmed ? 1.5 : 0,
                  borderRadius: 1,
                  border: fieldErrors.medicalRecordsConfirmed ? '1px solid' : 'none',
                  borderColor: 'error.main',
                  bgcolor: fieldErrors.medicalRecordsConfirmed ? 'error.50' : 'transparent',
                  mb: 2
                }}>
                  <DmsCheckBox
                    label={`${intl.get("MedicalRecordsConfirmation").d("Medical records have been archived with each prescription, including pregnancy exclusion proof, one copy of the prescription, and contraceptive methods guidance.")} *`}
                    labelComponent={(props) => <Typography variant="body2" {...props} />}
                    checked={medicalRecordsConfirmed}
                    onChange={(e) => {
                      setMedicalRecordsConfirmed(e.target.checked);
                      if (fieldErrors.medicalRecordsConfirmed) setFieldErrors(prev => ({ ...prev, medicalRecordsConfirmed: null }));
                    }}
                    name="medicalRecordsConfirmed"
                  />
                  {fieldErrors.medicalRecordsConfirmed && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {fieldErrors.medicalRecordsConfirmed}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : normalizeGender(patientData?.gender) === getGenderEnum().F.value ? (
              <Box sx={{ mb: 3 }}>
                <DmsAlertInfo message={intl.get("NoPregnancyPotential").d("Patient without pregnancy potential. Prescription validity: 20 days from the prescription date.")} />
              </Box>
            ) : null}

            {/* Non-female patients or after pregnancy section */}
            {normalizeGender(patientData?.gender) !== getGenderEnum().F.value && (
              <Box sx={{ mb: 3 }}>
                <DmsAlertInfo message={intl.get("MalePatientInfo").d("Male patient. Prescription validity: 20 days from the prescription date.")} />
              </Box>
            )}

            {/* Prescription and Expiration Date Fields - Now shown for all */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <DmsCalendar.DmsFlexiblePicker
                  pickerType="date"
                  label={`${intl.get("prescription_date").d("Prescription Date")} *`}
                  value={prescriptionDate}
                  onChange={(newValue) => {
                    setPrescriptionDate(newValue);
                    if (fieldErrors.prescriptionDate) setFieldErrors(prev => ({ ...prev, prescriptionDate: null }));
                  }}
                  format="DD/MM/YYYY"
                  sx={{ width: "100%" }}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      error: !!fieldErrors.prescriptionDate,
                      helperText: fieldErrors.prescriptionDate
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DmsCalendar.DmsFlexiblePicker
                  pickerType="date"
                  label={intl.get("ExpirationDate").d("Expiration Date")}
                  value={expirationDate}
                  format="DD/MM/YYYY"
                  sx={{ width: "100%" }}
                  disabled
                  helperText={isFemaleWithPotential
                    ? intl.get("ExpirationDateHelperFemale").d("Calculada automaticamente: 7 dias a partir da data do teste de gravidez")
                    : intl.get("ExpirationDateHelperOther").d("Calculated automatically")
                  }
                />
              </Grid>
            </Grid>

            {posologies.length > 0 ? (
              <DmsAutocomplete
                key={`posology-${posologies.length}`}
                label={`${intl.get("Posology").d("Posology")} *`}
                name="selectedPosology"
                value={selectedPosology}
                onChange={(value) => {
                  setSelectedPosology(value);
                  if (fieldErrors.posology) setFieldErrors(prev => ({ ...prev, posology: null }));
                }}
                options={posologySelectOptions}
                fullWidth
                sx={{ mb: 2 }}
                error={!!fieldErrors.posology}
                helperText={fieldErrors.posology}
              />
            ) : (
              <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
            )}

            {surplusValidation.blocked && (
              <Box sx={{ mb: 2 }}>
                <DmsAlertDanger message={surplusValidation.message} />
              </Box>
            )}

            {surplusValidation.capsuleReturnWarning && (
              <Box sx={{ mb: 2 }}>
                <DmsAlertWarning message={surplusValidation.message} />
              </Box>
            )}

            {selectedPosology && (
              <DmsTextField
                label={intl.get("AdditionalNotes").d("Additional Notes")}
                name="dosageNotes"
                fullWidth
                multiline
                rows={2}
                value={dosageNotes}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 180) {
                    setDosageNotes(newValue);
                    if (newValue.trim().length > 0 && allowAlternativeDispensation) {
                      setAllowAlternativeDispensation(false);
                      setDosageRectificationTermAccepted(false);
                    }
                  }
                }}
                placeholder={intl.get("AdditionalNotesPlaceholder").d("Optional notes about the posology")}
                helperText={`${dosageNotes.length}/180`}
                inputProps={{ maxLength: 180 }}
                sx={{ mb: 2 }}
              />
            )}

            <DmsAutocomplete
              label={`${intl.get("Indication").d("Indication")} *`}
              name="indication"
              value={indication}
              onChange={(value) => {
                setIndication(value);
                if (fieldErrors.indication) setFieldErrors(prev => ({ ...prev, indication: null }));
              }}
              options={indicationSelectOptions}
              fullWidth
              sx={{ mb: 2 }}
              error={!!fieldErrors.indication}
              helperText={fieldErrors.indication}
            />

            {indication === IndicationEnum.OTHER && (
              <DmsTextField
                label={`${intl.get("OtherIndicationText").d("Specify Other Indication")} *`}
                fullWidth
                multiline
                rows={2}
                value={indicationOther}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 115) {
                    setIndicationOther(newValue);
                    if (fieldErrors.indicationOther) setFieldErrors(prev => ({ ...prev, indicationOther: null }));
                  }
                }}
                placeholder={intl.get("OtherIndicationPlaceholder").d("Please specify the indication")}
                error={!!fieldErrors.indicationOther}
                helperText={
                  fieldErrors.indicationOther ||
                  `${intl.get("OtherIndicationHelper").d("Describe the indication for this prescription")} (${indicationOther.length}/115)`
                }
                inputProps={{ maxLength: 115 }}
                sx={{ mt: 2, mb: 2 }}
              />
            )}
            {showInstitutionSelect && (
              <UserInstitutionSelect
                label={`${intl.get("Institution").d("Institution")} *`}
                value={institution}
                onChange={(value) => {
                  setInstitution(value);
                  if (fieldErrors.institution) setFieldErrors(prev => ({ ...prev, institution: null }));
                }}
                required
                fullWidth
                error={!!fieldErrors.institution}
                helperText={fieldErrors.institution}
                showApprovalStatus={true}
              />
            )}

            {selectedInstitutionInactive && (
              <Box sx={{ mt: 3 }}>
                <DmsAlertWarning
                  message={
                    isInstitutionInactive
                      ? intl.get("Prescription.InstitutionInactive.Warning")
                        .d("Instituição inativa. Não é possível criar prescrições para esta instituição.")
                      : doctorDeclarationMissing
                        ? intl.get("Prescription.DeclarationRequired.Warning")
                          .d("Você precisa aceitar a Declaração de Vínculo Institucional para esta instituição antes de prescrever. Acesse Configurações de Perfil > Instituições para aceitar.")
                        : institutionApprovalStatus
                          ? intl.get("Prescription.UserNotApproved.Warning")
                            .d("Aguardando aprovação do usuário. Não é possível criar prescrições para esta instituição.")
                          : intl.get("Prescription.InstitutionPendingApproval.Warning")
                            .d("Aguardando aprovação da instituição. Não é possível criar prescrições para esta instituição.")
                  }
                />
              </Box>
            )}

            {/* CRM Selector - Only show if doctor has multiple CRMs */}
            {doctorInfo?.available_crms && doctorInfo.available_crms.length > 0 && (
              <DmsSelect
                label={intl.get("SelectCRM").d("CRM for Prescription")}
                options={doctorInfo.available_crms.map(crm => ({
                  value: crm.pk,
                  label: `${crm.crm} / ${crm.crm_state}${crm.is_primary ? ` (${intl.get("Primary").d("Primary")})` : ''}`
                }))}
                value={selectedCrm?.pk || ''}
                onChange={(value) => {
                  const selected = doctorInfo.available_crms.find(
                    c => c.pk === value || c.pk === Number(value)
                  );
                  setSelectedCrm(selected || null);
                }}
                fullWidth
                sx={{ mt: 2, mb: 2 }}
              />
            )}

            <Box sx={{
              display: "block",
              width: "100%",
              mt: 2,
              mb: 2,
              p: fieldErrors.liabilityWaiverSigned ? 1.5 : 0,
              borderRadius: 1,
              border: fieldErrors.liabilityWaiverSigned ? '1px solid' : 'none',
              borderColor: 'error.main',
              bgcolor: fieldErrors.liabilityWaiverSigned ? 'error.50' : 'transparent'
            }}>
              <DmsCheckBox
                label={intl.get("CommitToSignDocuments").d("Liability Term: I commit to print and collect the signatures of the 3 generated documents.")}
                labelComponent={(props) => <Typography variant="caption" {...props} />}
                checked={liabilityWaiverSigned}
                onChange={(e) => {
                  setLiabilityWaiverSigned(e.target.checked);
                  if (fieldErrors.liabilityWaiverSigned) setFieldErrors(prev => ({ ...prev, liabilityWaiverSigned: null }));
                }}
                name="liabilityWaiverSigned"
              />
              {fieldErrors.liabilityWaiverSigned && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {fieldErrors.liabilityWaiverSigned}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "block", width: "100%", mt: 2, mb: 2 }}>
              <DmsCheckBox
                label=
                {intl.get("AllowAlternativeDispensationWithTerm").d("By accepting this term, I confirm that I authorize the adjustment of the dispensing and dosage of the medication Nuvyor (lenalidomide) according to the availability of stock of the presentations, provided that the prescribed dose and dosage are strictly respected.")}
                labelComponent={(props) => <Typography variant="caption" {...props} />}
                checked={allowAlternativeDispensation}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAllowAlternativeDispensation(checked);
                  // Auto-accept term when checkbox is checked
                  setDosageRectificationTermAccepted(checked);
                }}
                disabled={dosageNotes.trim().length > 0}
                name="allowAlternativeDispensation"
              />
              {dosageNotes.trim().length > 0 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                  {intl.get("AlternativeDispensationDisabledByNotes").d("This option is not available when additional posology notes are provided. Remove the notes to enable it.")}
                </Typography>
              )}
            </Box>


            <DmsButton
              variant="primary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={handleFinalizePrescription}
              text={intl.get("FinalizePrescription").d("Finalize Prescription")}
              disabled={
                !patient ||
                !selectedPosology ||
                !institution ||
                !indication ||
                !liabilityWaiverSigned ||
                blockPrescription ||
                surplusValidation.blocked ||
                selectedInstitutionInactive ||
                (isFemaleWithPotential && (
                  !lastPregnancyTestDate ||
                  !pregnancyTestConfirmed ||
                  !menstrualCycle ||
                  highlyEffectiveMethods.length === 0 ||
                  barrierMethods.length === 0 ||
                  !medicalRecordsConfirmed
                ))
              }
            />
          </DmsAltBox.Body>
        </DmsAltBox.Card>
      )}

      <DmsModal isVisible={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <DmsModal.Header title={intl.get("attention").d("Attention")} onClose={() => setShowErrorModal(false)} />
        <DmsModal.Body>
          <Typography>{errorModalMessage}</Typography>
        </DmsModal.Body>
        <DmsModal.Footer>
          <DmsButton variant="primary" text={intl.get("ok").d("OK")} onClick={() => setShowErrorModal(false)} />
        </DmsModal.Footer>
      </DmsModal>

      <DmsModal isVisible={showPatientNotFoundModal} onClose={() => setShowPatientNotFoundModal(false)}>
        <DmsModal.Header title={intl.get("patient_not_found_title").d("Patient Not Found")} onClose={() => setShowPatientNotFoundModal(false)} />
        <DmsModal.Body>
          <Typography variant="body1">
            {intl.get("patient_not_found_message").d("No patient found with the provided CPF and Date of Birth. Please verify the information and try again.")}
          </Typography>
        </DmsModal.Body>
        <DmsModal.Footer>
          <DmsButton variant="primary" text={intl.get("ok").d("OK")} onClick={() => setShowPatientNotFoundModal(false)} fullWidth />
        </DmsModal.Footer>
      </DmsModal>

      <DmsModal isVisible={showActivePrescriptionModal} onClose={() => {
        setShowActivePrescriptionModal(false);
        setJustification('');
      }}>
        <DmsModal.Header title={intl.get("recent_prescription_error_title").d("Recent Prescription Found")} onClose={() => {
          setShowActivePrescriptionModal(false);
          setJustification('');
        }} />
        <DmsModal.Body>
          <Box sx={{ py: 2 }}>
            {activePrescriptionInfo?.errorMessage ? (
              // Show backend error message if available (includes days remaining)
              <Typography variant="body1" gutterBottom>
                {activePrescriptionInfo.errorMessage}
              </Typography>
            ) : (
              // Fallback to generic message
              <Typography variant="body1" gutterBottom>
                {intl.get("active_prescription_error_message").d("Patient has an active prescription that is still valid. Cannot create a new prescription.")}
              </Typography>
            )}

            {activePrescriptionInfo?.prescription && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {intl.get("prescription_details").d("Prescription Details:")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("prescription_date").d("Prescription Date")}:</strong>{' '}
                      {activePrescriptionInfo.prescription.prescription_date ? dayjs(activePrescriptionInfo.prescription.prescription_date).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("created_date").d("Created Date")}:</strong>{' '}
                      {activePrescriptionInfo.prescription.created_on ? dayjs(activePrescriptionInfo.prescription.created_on).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("status").d("Status")}:</strong>{' '}
                      {activePrescriptionInfo?.isExpiredNotDispensed ? intl.get("Expired").d("Expired") :
                        activePrescriptionInfo.prescription.status === PRESCRIPTION_STATUS.DISPENSED ? intl.get("Dispensed").d("Dispensed") :
                          activePrescriptionInfo.prescription.status === PRESCRIPTION_STATUS.ACTIVE ? intl.get("Active").d("Active") :
                            intl.get("Other").d("Other")}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activePrescriptionInfo?.isExpiredNotDispensed ? (
              <Box sx={{ mt: 3 }}>
                <DmsAlertWarning message={intl.get("active_expired_not_dispensed_prescription").d(
                  "Expired prescription (not dispensed). Please provide a justification for non-dispensation."
                )} />
                <DmsSelect
                  label={intl.get("not_dispensed_justification_label").d("Reason for non-dispensation")}
                  fullWidth
                  value={justification}
                  onChange={(value) => { setJustification(value); setNuvyorLocation(''); }}
                  sx={{ mt: 2 }}
                  options={getNotDispensedReasonOptions()}
                />
                {justification === NotDispensedReasonEnum.RECEIVED_NUVYOR && (
                  <DmsTextField
                    label={intl.get("NuvyorLocationLabel").d("Where did the patient get the Nuvyor?")}
                    fullWidth
                    value={nuvyorLocation}
                    onChange={(e) => setNuvyorLocation(e.target.value)}
                    sx={{ mt: 2 }}
                    required
                  />
                )}
              </Box>
            ) : activePrescriptionInfo?.prescription?.status === PRESCRIPTION_STATUS.DISPENSED ? (
              <Box sx={{ mt: 3 }}>
                <DmsAlertDanger message={intl.get("cannot_cancel_dispensed_prescription").d("This prescription has already been dispensed and cannot be canceled. Wait 19 days from the prescription creation date to create a new one.")} />
              </Box>
            ) : activePrescriptionInfo?.canCancel ? (
              <Box sx={{ mt: 3 }}>
                <DmsAlertInfo message={intl.get("can_cancel_own_prescription").d("You are the doctor who created this prescription. You can cancel it to create a new one.")} />
                <DmsTextField
                  label={intl.get("cancellation_justification").d("Justification for cancellation")}
                  multiline
                  rows={4}
                  fullWidth
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder={intl.get("enter_cancellation_reason").d("Enter the reason for canceling this prescription")}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 3 }}>
                <DmsAlertWarning message={intl.get("cannot_cancel_other_doctor_prescription").d("This prescription was created by another doctor. Only the prescribing doctor can cancel it.")} />
              </Box>
            )}
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          {activePrescriptionInfo?.isExpiredNotDispensed ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <DmsButton variant="secondary" text={intl.get("Close").d("Close")} onClick={() => {
                setShowActivePrescriptionModal(false);
                setJustification('');
                setNuvyorLocation('');
              }} />
              <DmsButton
                variant="primary"
                text={intl.get("submit_with_justification").d("Submit with Justification")}
                onClick={async () => {
                    if (!justification?.trim()) {
                      setErrorModalMessage(intl.get("not_dispensed_justification_required").d(
                        "Justification for non-dispensation is required."
                      ));
                      setShowErrorModal(true);
                      return;
                    }
                    if (justification === NotDispensedReasonEnum.RECEIVED_NUVYOR && !nuvyorLocation?.trim()) {
                      setErrorModalMessage(intl.get("NuvyorLocationLabel").d("Where did the patient get the Nuvyor?"));
                      setShowErrorModal(true);
                      return;
                    }
                    try {
                      const locationSuffix = nuvyorLocation?.trim() ? ` | ${nuvyorLocation.trim()}` : '';
                      const response = await fetchData('/prescription/mark-not-dispensed', 'POST', true, {
                        prescription_pk: activePrescriptionInfo.prescription.pk,
                        justification: `${justification.trim()}${locationSuffix}`,
                      });
                      if (response.status === HTTP_STATUS.OK_200) {
                        setShowActivePrescriptionModal(false);
                        setJustification('');
                        setNuvyorLocation('');
                        setHasExpiredNotDispensed(false);
                        setBlockPrescription(false);
                        setHasActivePrescription(false);
                        setActivePrescriptionPk(null);
                        setPrescriptionExpired(false);
                      } else {
                        const errMsg = response.data?.detail || 'Error';
                        setErrorModalMessage(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
                        setShowErrorModal(true);
                      }
                    } catch (error) {
                      setErrorModalMessage(intl.get("not_dispensed_justification_error").d("Error submitting justification. Please try again."));
                      setShowErrorModal(true);
                    }
                  }}
                  fullWidth
                  disabled={
                    !justification?.trim() ||
                    (justification === NotDispensedReasonEnum.RECEIVED_NUVYOR && !nuvyorLocation?.trim())
                  }
                />
            </Box>
          ) : activePrescriptionInfo?.prescription?.status === PRESCRIPTION_STATUS.DISPENSED ? (
            <DmsButton variant="primary" text={intl.get("ok").d("OK")} onClick={() => {
              setShowActivePrescriptionModal(false);
              setJustification('');
            }} fullWidth />
          ) : activePrescriptionInfo?.canCancel ? (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DmsButton variant="secondary" text={intl.get("Close").d("Close")} onClick={() => {
                  setShowActivePrescriptionModal(false);
                  setJustification('');
                }} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <DmsButton
                  variant="danger"
                  text={intl.get("cancel_prescription").d("Cancel Prescription")}
                  onClick={async () => {
                    if (!justification || justification.trim().length === 0) {
                      setErrorModalMessage(intl.get("cancellation_justification_required").d("Cancellation justification is required."));
                      setShowErrorModal(true);
                      return;
                    }

                    try {
                      const response = await fetchData('/prescription/cancel', 'POST', true, {
                        prescription_pk: activePrescriptionInfo.prescription.pk,
                        cancellation_justification: justification
                      });

                      if (response.status === HTTP_STATUS.OK_200) {
                        setShowActivePrescriptionModal(false);
                        setJustification('');
                        setErrorModalMessage(intl.get("prescription_canceled_success").d("Prescription canceled successfully! You can now create a new prescription."));
                        setShowErrorModal(true);

                        if (useCpfSearch && cpf && dob) {
                          const cleanedCpf = cpf.replace(/\D/g, '');
                          const formattedDob = dob.format('YYYY-MM-DD');
                          const patientResponse = await fetchData(`/patient/find_by_cpf_dob?cpf=${cleanedCpf}&dob=${formattedDob}`, 'GET', true);
                          if (patientResponse.status === 200) {
                            let foundPatient = patientResponse.data.data || patientResponse.data || patientResponse;
                            if (foundPatient && foundPatient.pk) {
                              // Fetch prescriptions after cancellation to refresh blocking status
                              try {
                                const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${foundPatient.pk}?show_all=true`, 'GET', true);
                                if (prescriptionsResponse.status === 200) {
                                  const prescriptions = prescriptionsResponse.data?.data || [];
                                  foundPatient = {
                                    ...foundPatient,
                                    prescription: prescriptions
                                  };
                                }
                              } catch (error) {
                                console.error('Error fetching patient prescriptions:', error);
                              }
                              processSelectedPatient(foundPatient);
                            }
                          }
                        } else {
                          const patientsResponse = await fetchData('/doctor/all_patients', 'GET', true);
                          if (patientsResponse.status === 200) {
                            const patientsData = patientsResponse.data.data?.rows || patientsResponse.data.data || [];
                            setPatients(patientsData);
                            const updatedPatient = patientsData.find(p => p.pk === patient);
                            if (updatedPatient) {
                              handlePatientChange(updatedPatient.pk);
                            }
                          }
                        }
                      }
                    } catch (error) {
                      console.error('Error canceling prescription:', error);
                      setErrorModalMessage(intl.get("prescription_cancel_error").d("Error canceling prescription. Please try again."));
                      setShowErrorModal(true);
                    }
                  }}
                  fullWidth
                  disabled={!justification || justification.trim().length === 0}
                />
              </Grid>
            </Grid>
          ) : (
            <DmsButton variant="primary" text={intl.get("ok").d("OK")} onClick={() => {
              setShowActivePrescriptionModal(false);
              setJustification('');
            }} fullWidth />
          )}
        </DmsModal.Footer>
      </DmsModal>

      <DmsModal isVisible={showLegalGuardianModal} onClose={() => {
        setShowLegalGuardianModal(false);
        setLegalGuardianApproval(false);
      }}>
        <DmsModal.Header
          title={intl.get("legal_guardian_approval_required").d("Legal Guardian Approval Required")}
          onClose={() => {
            setShowLegalGuardianModal(false);
            setLegalGuardianApproval(false);
          }}
        />
        <DmsModal.Body>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              {intl.get("legal_guardian_approval_message").d("This patient has a legal guardian registered. Approval from the legal guardian is required to proceed with the prescription.")}
            </Typography>

            {patientData?.responsible_legal && patientData.responsible_legal.length > 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main', mb: 2 }}>
                  {intl.get("legal_guardian_data").d("Legal Guardian Data")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                      {intl.get("name").d("Name")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {patientData.responsible_legal[0].name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                      {intl.get("CPF").d("CPF")}
                    </Typography>
                    <Typography variant="body1">
                      {patientData.responsible_legal[0].cpf}
                    </Typography>
                  </Grid>
                  {patientData.responsible_legal[0].rg && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {intl.get("rg").d("RG")}
                      </Typography>
                      <Typography variant="body1">
                        {patientData.responsible_legal[0].rg}
                      </Typography>
                    </Grid>
                  )}
                  {patientData.responsible_legal[0].phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {intl.get("phone").d("Phone")}
                      </Typography>
                      <Typography variant="body1">
                        {patientData.responsible_legal[0].phone}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <DmsCheckBox
                label={intl.get("confirm_legal_guardian_approval").d("I confirm that the legal guardian has approved this prescription and is aware of all the terms and conditions.")}
                labelComponent={(props) => <Typography variant="body2" {...props} />}
                checked={legalGuardianApproval}
                onChange={(e) => setLegalGuardianApproval(e.target.checked)}
                name="legalGuardianApproval"
              />
            </Box>
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DmsButton
                variant="secondary"
                text={intl.get("Cancel").d("Cancel")}
                onClick={() => {
                  setShowLegalGuardianModal(false);
                  setLegalGuardianApproval(false);
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <DmsButton
                variant="primary"
                text={intl.get("confirm_and_proceed").d("Confirm and Proceed")}
                onClick={() => {
                  if (legalGuardianApproval) {
                    setShowLegalGuardianModal(false);
                    handleFinalizePrescription();
                  } else {
                    setErrorModalMessage(intl.get("legal_guardian_approval_required_checkbox").d("Please confirm that the legal guardian has approved the prescription."));
                    setShowErrorModal(true);
                  }
                }}
                fullWidth
                disabled={!legalGuardianApproval}
              />
            </Grid>
          </Grid>
        </DmsModal.Footer>
      </DmsModal>

      <DmsModal isVisible={showSuccessModal} onClose={handleCloseSuccessModal}>
        <DmsModal.Header
          title={intl.get("prescription_created_success").d("Prescription Created Successfully")}
          onClose={handleCloseSuccessModal}
        />
        <DmsModal.Body>
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40, mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    {intl.get("prescription_created_message").d("The prescription has been created successfully!")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get("prescription_print_instruction").d("Click confirm to open the document for printing.")}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {intl.get("prescription_info").d("Important information:")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  • {intl.get("prescription_3_copies").d("PDF contains 3 prescription copies")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  • {intl.get("prescription_signatures_required").d("Print and collect required signatures")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  • {intl.get("prescription_terms_included").d("Includes liability waiver and consent terms")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          <DmsButton
            variant="primary"
            text={intl.get("ConfirmAndPrint").d("Confirm and Print")}
            fullWidth
            onClick={async () => {
              if (createdPrescriptionPk) {
                try {
                  await openPrescriptionPdfForPrint(createdPrescriptionPk);
                } catch (error) {
                  console.error('Error opening PDF:', error);
                }
              }
              handleCloseSuccessModal();
            }}
          />
        </DmsModal.Footer>
      </DmsModal>

      {/* Pregnancy Potential Warning Modal */}
      <DmsModal
        isVisible={showPregnancyPotentialWarning}
        onClose={() => setShowPregnancyPotentialWarning(false)}
      >
        <DmsModal.Header
          title={intl.get("pregnancy_potential_warning_title").d("Update Patient Record")}
          onClose={() => setShowPregnancyPotentialWarning(false)}
        />
        <DmsModal.Body>
          <Box sx={{ py: 2 }}>
            <DmsAlertWarning
              message={intl.get("pregnancy_potential_stored_on_patient").d(
                "Pregnancy potential is stored on the patient's permanent medical record, not on individual prescriptions."
              )}
            />
            <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
              {intl.get("pregnancy_potential_redirect_explanation").d(
                "To change the pregnancy potential status, you need to update the patient's record. Would you like to go to the patient edit page now?"
              )}
            </Typography>
            <DmsAlertInfo
              message={intl.get("pregnancy_potential_close_prescription").d(
                "If you proceed, the current prescription form will be closed. Make sure to save any important information."
              )}
            />
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DmsButton
                variant="secondary"
                text={intl.get("stay_on_prescription").d("Stay on Prescription")}
                onClick={() => setShowPregnancyPotentialWarning(false)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DmsButton
                variant="primary"
                text={intl.get("go_to_patient_edit").d("Edit Patient Record")}
                onClick={() => {
                  setShowPregnancyPotentialWarning(false);
                  handleOpenEditPatient();
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </DmsModal.Footer>
      </DmsModal>

      {/* Training Warning Modal */}
      <TrainingModal
        isVisible={showTrainingWarningModal}
        onClose={() => setShowTrainingWarningModal(false)}
        message={trainingWarningMessage}
        instructionText={intl.get("training_access_instruction").d(
          "Please navigate to the Training Materials section to complete the required training."
        )}
      />

      {/* Prescription Preview Modal */}
      <PrescriptionPreviewModal
        isVisible={showPreviewModal}
        onClose={() => !creatingPrescription && setShowPreviewModal(false)}
        onConfirm={handleConfirmPrescription}
        prescriptionData={previewPrescriptionData}
        patientData={patientData}
        doctorInfo={doctorInfo}
        selectedCrm={selectedCrm}
        institutions={institutions}
        allowAlternativeDispensation={allowAlternativeDispensation}
        highlyEffectiveMethodOptions={highlyEffectiveMethodOptions}
        barrierMethodOptions={barrierMethodOptions}
        isLoading={creatingPrescription}
      />

      {/* Quantity Alert Modal */}
      <PrescriptionQuantityAlertModal
        isVisible={showQuantityAlertModal}
        onClose={() => {
          setShowQuantityAlertModal(false);
          setAbovePackageInsertConfirmed(false);
        }}
        onConfirm={() => {
          setAbovePackageInsertConfirmed(true);
          setShowQuantityAlertModal(false);
          setShowPreviewModal(true);
        }}
        dosageDescription={previewPrescriptionData?.dosage_description}
      />

      {/* Patient Edit Modal */}
      <PatientDetailModal
        open={editPatientModalOpen}
        onClose={() => {
          setEditPatientModalOpen(false);
          setPatientDetailsForEdit(null);
        }}
        patient={patientDetailsForEdit}
        loading={loadingPatientDetails}
        onSave={handleSavePatient}
        onPatientUpdated={async () => {
          // Reload contraceptive methods after PatientDetailModal finishes saving everything
          if (patientData?.pk) {
            const hasPotential = normalizeGender(patientData.gender) === getGenderEnum().F.value && patientData.has_pregnancy_potential;
            if (hasPotential) {
              await fetchPatientContraceptiveMethods(patientData.pk);
            } else {
              setHighlyEffectiveMethods([]);
              setBarrierMethods([]);
            }
          }
        }}
        canEdit={true}
        startInEditMode={true}
      />

      {/* Dosage Rectification Term Dialog */}
      <LegalDocumentDialog
        open={termDialogOpen}
        onClose={() => setTermDialogOpen(false)}
        document={termDocument}
        loading={loadingTerm}
      />
    </>
  );
};
